import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleModal, toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { addCardData, updateCardsList } from 'apps/auth/actions/cardsAction';
import { addExtras, updateExtraByName } from 'apps/auth/actions/extraAction';
import { setFieldError, setNewForm, updateFields, updateFieldValues } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import CardScreen from 'apps/auth/components/cards/CardScreen';
import { Button } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import Form from 'apps/auth/components/form-multi/Form';
import SmsConfirmation from 'apps/auth/components/utils/SmsConfirmation';
import { SMS_CONFIRMATION } from 'apps/auth/constants/ExtraNames';
import { ADD_SOURCE_BANK_CARD } from 'apps/auth/constants/formName';
import { AUTH_BANK_CARD_ADD, AUTH_MY_CARDS_URL, AUTH_PLUM_BANK_CARD_ADD_ACTION, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { isObject } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { ApiService } from 'apps/auth/services/apiService';

const AddPlumBankCard = (props) => {
    const { smsConfirmation, notNow, redirectUrl, cardPan } = useExtra();
    const {
        cardAdded,
        addCardSmsConfirmation,
        enterSmsTitle,
        smsConfirmationResendInfo,
        plumCardsAddBankCardButton,
        cardsAddBankCardTitle,
        plumCardsAddBankCardTitle,
        cardsAddBankCardText,
    } = usePageMessages();
    const punName = useRef(null);
    const punValue = useRef(null);
    const cardExpiredName = useRef(null);
    const cardExpiredValue = useRef(null);

    useEffect(() => {
        if (!isObject(props.fields)) {
            props.toggleSpinner(true);
            apiFetchData(AUTH_BANK_CARD_ADD)
                .then((json) => {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.addExtras(json.extra);
                    }
                    props
                        .addCardData(json.cardForm.card)
                        .then(() => props.setNewForm(ADD_SOURCE_BANK_CARD, getFields(json.cardForm)).then(() => props.toggleSpinner(false)));
                })
                .catch(() => props.toggleSpinner(false));
        } else {
            const clearFields = { ...props.fields };
            Object.values(clearFields).forEach((field) => {
                field.value = '';
                field.isError = false;
            });
            props.updateFields(ADD_SOURCE_BANK_CARD, clearFields);
        }
        return () => {
            props.updateExtraByName(SMS_CONFIRMATION, null);
        };
    }, []);

    const getFields = (cardForm) => {
        const fields = {};
        if (isObject(cardForm)) {
            const card = cardForm.card;
            punName.current = card.pan.name;
            fields[card.pan.name] = card.pan;
            if (isObject(card.cardExpired)) {
                cardExpiredName.current = card.cardExpired.name;
                fields[card.cardExpired.name] = card.cardExpired;
            }
            if (isObject(card.cvv)) {
                fields[card.cvv.name] = card.cvv;
            }
            if (Array.isArray(cardForm.otherFields)) {
                cardForm.otherFields.forEach((otherField) => (fields[otherField.name] = otherField));
            }
        }
        return fields;
    };

    const submitHandler = () => {
        punValue.current = props.fields[punName.current]?.value;
        cardExpiredValue.current = props.fields[cardExpiredName.current]?.value;
    };

    const canSenSms = () => {
        return (
            smsConfirmation &&
            punValue.current === props.fields[punName.current]?.value &&
            cardExpiredValue.current === props.fields[cardExpiredName.current]?.value
        );
    };

    if (redirectUrl) {
        setTimeout(() => {
            window.location.href = redirectUrl;
        }, 2000);
        return <InfoPage animatedIconSuccess headerText={cardAdded} />;
    } else if (props.showEnterSms) {
        return (
            <SmsConfirmation
                smsConfirmation={smsConfirmation}
                resetInterval={smsConfirmation?.secondsBeforeExpiration}
                confirmAction={'/card/sms-confirm.action'}
                resendAction={AUTH_PLUM_BANK_CARD_ADD_ACTION}
                redirectUrl={notNow ? AUTH_SEND_MAIN_LINK : AUTH_MY_CARDS_URL}
                smsLength={6}
                customResendAction={() => {
                    ApiService.fetchWithoutSpinner(AUTH_PLUM_BANK_CARD_ADD_ACTION, props.fields, (json) => {
                        if (json.extra) {
                            props.addExtras(json.extra);
                        }
                        if (json.messageBodyMessages) {
                            props.updateMessages(json.messageBodyMessages);
                        }
                    });
                }}
                headerText={addCardSmsConfirmation}
                headerGrayText={enterSmsTitle}
                smsResendText={smsConfirmationResendInfo}
            />
        );
    } else if (props.fields) {
        return (
            <>
                <HeaderPanel title={cardPan ? plumCardsAddBankCardTitle : cardsAddBankCardTitle} subtitle={cardsAddBankCardText} />
                <Form getCanonicalPath={true} formName={ADD_SOURCE_BANK_CARD} action={`${AUTH_PLUM_BANK_CARD_ADD_ACTION}`} onSubmitHandler={submitHandler}>
                    <CardScreen formName={ADD_SOURCE_BANK_CARD} />
                    <FixedBottomPanel isRightAligned>
                        <Button
                            type="submit"
                            raised={true}
                            caption={plumCardsAddBankCardButton}
                            onClick={(e) => {
                                if (canSenSms()) {
                                    e.preventDefault();
                                    props.toggleEnterSms(true);
                                }
                            }}
                        />
                    </FixedBottomPanel>
                </Form>
            </>
        );
    }
    return false;
};

const mapStateToProps = (state) => {
    return {
        fields: state.form[ADD_SOURCE_BANK_CARD]?.fields,
        card: state.cards.cardFields,
    };
};
export default connect(mapStateToProps, {
    setNewForm,
    updateFields,
    updatePageMessages,
    toggleSpinner,
    toggleModal,
    updateFieldValues,
    addCardData,
    addExtras,
    updateExtraByName,
    setFieldError,
    updateCardsList,
    updateMessages,
})(AddPlumBankCard);
